import { ADDIN_NAMESPACE } from '../config/addinNamespace';

export interface ParsedBifrostFormula {
  functionType: string;
  formulaName: string;
  params: (string | number)[];
  /**
   * Whether each entry in `params` was written quoted in the cell, positionally
   * aligned with it.
   *
   * Needed to rebuild a call faithfully: `"C1"` and `C1` both parse to the
   * string "C1", but the first is a customer code and the second is a reference
   * to cell C1. Without this, regenerating the formula turns one into the other.
   */
  paramsQuoted: boolean[];
}

/**
 * Only two functions are registered now: LEDGER for a value and ENTRIES for the
 * rows behind it. Both resolve the formula by name, so the call itself no
 * longer says which data source it hits — that lives on the stored definition.
 */
const FUNC_TYPE_MAP: Record<string, string> = {
  LEDGER: 'ledger',
  ENTRIES: 'ledger',
};

/**
 * Matches one of this build's function calls anywhere in a formula.
 *
 * The pattern this replaces was `^=@?…BIFROST[._](LEDGER|…)`, wrong in two
 * ways. It was anchored to the start, so `=-BIFROST.LEDGER(…)` and every
 * nested call were invisible. And `BIFROST[._]` consumed the underscore of
 * `BIFROST_TEST.LEDGER` before demanding a function name, so it matched
 * nothing outside the prod namespace — recalc reported "no formulas found"
 * for every workbook in the TEST and DEV builds.
 *
 * Built from ADDIN_NAMESPACE so it can't drift from the namespace the build
 * actually registers. The leading non-word-character alternative admits `=`,
 * `-`, `(`, `@` and the `_xludf.` / `_xlfn._xlws.` prefixes Excel adds.
 */
export const BIFROST_FORMULA_RE = new RegExp(
  `(?:^|[^A-Za-z0-9_])${ADDIN_NAMESPACE.replace(/[^A-Za-z0-9_]/g, '\\$&')}` +
  `\\.(LEDGER|ENTRIES)\\s*\\(`,
  'i'
);

function parseParamValue(raw: string): string | number {
  if (/^\d+(\.\d+)?$/.test(raw)) return Number(raw);
  return raw;
}

/**
 * Pull the function type, formula name and arguments out of a cell formula.
 * Returns null when the formula holds no call from this build's namespace.
 */
export function parseBifrostFormula(formulaText: string): ParsedBifrostFormula | null {
  const match = BIFROST_FORMULA_RE.exec(formulaText);
  if (!match) return null;
  const functionType = FUNC_TYPE_MAP[match[1].toUpperCase()] || 'ledger';

  // The match ends on the call's own '(', so scan from there to the paren that
  // balances it. indexOf('(') / lastIndexOf(')') were only ever good enough
  // because the formula was assumed to be one bare call; given
  // `=SUM(BIFROST.LEDGER(…), 5)` they take SUM's arguments instead.
  const argsStart = match.index + match[0].length;
  let depth = 1;
  let inQuote = false;
  let argsEnd = -1;
  for (let i = argsStart; i < formulaText.length; i++) {
    const ch = formulaText[i];
    if (ch === '"') { inQuote = !inQuote; continue; }
    if (inQuote) continue;
    if (ch === '(') depth++;
    else if (ch === ')' && --depth === 0) { argsEnd = i; break; }
  }
  if (argsEnd < 0) return null;

  const argsStr = formulaText.substring(argsStart, argsEnd);
  const params: (string | number)[] = [];
  const quoted: boolean[] = [];
  let current = '';
  let inQuotes = false;
  let wasQuoted = false;

  // A quoted argument stays a string even when it looks like a number. The
  // pre-calculated results are filed under getFormulaKey(), which is
  // JSON.stringify'd, and the custom function receives "4000" from Excel as a
  // string — so coercing it to 4000 here produced a key the function never
  // looked up, and every pre-calculated value was silently wasted.
  const flush = () => {
    const raw = current.trim();
    if (wasQuoted) { params.push(raw); quoted.push(true); }
    else if (raw) { params.push(parseParamValue(raw)); quoted.push(false); }
    current = '';
    wasQuoted = false;
  };

  for (let i = 0; i < argsStr.length; i++) {
    const ch = argsStr[i];
    if (ch === '"') { inQuotes = !inQuotes; wasQuoted = true; continue; }
    if (ch === ',' && !inQuotes) { flush(); continue; }
    current += ch;
  }
  if (current.trim() || wasQuoted) flush();

  if (params.length === 0) return null;
  const formulaName = String(params.shift());
  quoted.shift();
  return { functionType, formulaName, params, paramsQuoted: quoted };
}
